import { Box, Grid } from '@mui/material';
import { useState, useEffect, memo, useCallback } from 'react';
import { getAssociations } from 'src/shared/services/associationService';
import { getDistricts } from 'src/shared/services/districtService';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ControlledAutocompleteDropdown } from '../form/controlledAutocompleteDropdown';

type AssociationAndDistrictSelectEventProps = {
  hideDistrict: boolean;
  districtFormFieldName: string;
  associationFormFieldName: string;
  useGridLayout?: boolean;
  useLazyLoading?: boolean;
  // associationCallback: (val: Association | undefined) => void;
  // districtCallback: (val: District | undefined) => void;
};

export const AssociationAndDistrictSelectEvent = (props: AssociationAndDistrictSelectEventProps) => {
  const { hideDistrict, districtFormFieldName, associationFormFieldName, useGridLayout = false, useLazyLoading = false } = props;
  const [associations, setAssociations] = useState<Association[]>();
  const [districts, setDistricts] = useState<District[]>();

  const { getValues, setValue } = useFormContext();
  const { currentUser } = useGlobals();
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const dist: District | undefined = getValues(districtFormFieldName);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const result = await getDistricts(0, 10000, 'countyAss', 'asc', true);
        if (isMounted()) {
          const items = result.data.results.sort((a, b) => (a.countyAss > b.countyAss ? 1 : -1));
          setDistricts(items);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };

    if (!hideDistrict) {
      fetchDistricts();
    }
  }, [isMounted, hideDistrict]);

  const fetchData = useCallback(async () => {
    try {
      // Hämta på getvalue District
      const result = await getAssociations(dist?.countyAss_id ?? undefined);
      if (isMounted()) {
        const items = result.data.results.sort((a, b) => (a.localAss > b.localAss ? 1 : -1));
        setAssociations(items);

        // Har kunden bara en förening ska den vara förvald. Annars är det lätt att spara med
        // Alla, och ett evenemang utan förening visas inte på medlemmarnas Min sida.
        // Bara när inget distrikt är valt, en ensam förening inom ett distrikt är något annat,
        // och bara när fältet är tomt så att redigering av ett befintligt evenemang inte skrivs över.
        if (!dist && items.length === 1 && !getValues(associationFormFieldName)) {
          setValue(associationFormFieldName, items[0]);
        }
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    }
  }, [dist, isMounted, associationFormFieldName, getValues, setValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // const updateAssociation = (val: Association | undefined) => {
  //   // ConsoleHelper.log('updateAssociation', val);
  //   associationCallback(val);
  //   setLocalAssociation(val);
  // };

  // const updateDistrict = (val: District | undefined) => {
  //   // ConsoleHelper.log('updateDistrict', val);
  //   districtCallback(val);
  //   setLocalDistrict(val);
  // };

  // useEffect(() => {
  //   setLocalAssociation(association);
  // }, [association]);

  // useEffect(() => {
  //   setLocalDistrict(district);
  // }, [district]);

  ConsoleHelper.log('district', dist);
  const Layout = ({ children }) => {
    return useGridLayout ? (
      <Grid item xs>
        {children}
      </Grid>
    ) : (
      <Box>{children}</Box>
    );
  };

  return (
    <>
      {districts && districts.length > 0 && !hideDistrict && (
        <Layout>
          <Box
            sx={{
              ...(useGridLayout ? null : { mr: 2 }),
            }}
          >
            <ControlledAutocompleteDropdown<District>
              options={districts}
              nameProp='countyAss'
              placeholder={t('common:form.all')}
              name={districtFormFieldName ?? 'district'}
              label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:tableHeaders.district'))}
              {...(useGridLayout ? { componentWidth: '100%' } : null)}
              callback={() => setValue('association', undefined)}
            />
          </Box>
        </Layout>
      )}
      {associations && associations.length > 0 && (
        <Layout>
          <ControlledAutocompleteDropdown<Association>
            options={associations}
            nameProp='localAss'
            placeholder={t('common:form.all')}
            name={associationFormFieldName ?? 'association'}
            label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:tableHeaders.association'))}
            {...(useGridLayout ? { componentWidth: '100%' } : null)}
            callback={(val) => ConsoleHelper.log(val || undefined)}
          />
        </Layout>
      )}
    </>
  );
};

export const MemoizedAssociationAndDistrictSelect = memo(AssociationAndDistrictSelectEvent);
